import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography,
  Chip, Stack, CircularProgress, Button, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, AreaChart,
} from 'recharts';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';

function formatBps(bps) {
  if (bps === null || bps === undefined) return null;
  const abs = Math.abs(bps);
  if (abs >= 1e9) return (bps / 1e9).toFixed(2) + ' Gbps';
  if (abs >= 1e6) return (bps / 1e6).toFixed(2) + ' Mbps';
  if (abs >= 1e3) return (bps / 1e3).toFixed(1) + ' Kbps';
  return Math.round(bps) + ' bps';
}

function formatClock(clock) {
  const d = new Date(clock * 1000);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Cruza a edge com os hosts A/B pra encontrar o peer BGP, a iface OSPF ou o VRF
 * que realmente está ativo sobre esse link.
 */
function findEdgeProtocolDetails(edge, hostA, hostB) {
  if (!edge || !hostA || !hostB) return {};
  const kinds = edge.kinds || [];
  const result = { bgpPeerA: null, bgpPeerB: null, ospfIfaceA: null, ospfIfaceB: null, vrfA: null, vrfB: null };

  // BGP: procura nos bgp_peers de A um peer cujo IP está na subnet da edge OU
  // cujo connect_interface bate com o nome da iface A da edge.
  const subnet = edge.subnet;
  const ifaceA = edge.endpoints?.a?.iface;
  const ifaceB = edge.endpoints?.b?.iface;
  const ipA = edge.endpoints?.a?.ip;
  const ipB = edge.endpoints?.b?.ip;

  if (kinds.includes('bgp')) {
    result.bgpPeerA = (hostA.bgp_peers || []).find((p) => (
      (ipB && p.ip === ipB) || (ifaceA && p.connect_interface === ifaceA)
    )) || null;
    result.bgpPeerB = (hostB.bgp_peers || []).find((p) => (
      (ipA && p.ip === ipA) || (ifaceB && p.connect_interface === ifaceB)
    )) || null;
  }

  // OSPF: procura na lista de interfaces de A uma cujo nome bate com ifaceA
  if (kinds.includes('ospf_adj')) {
    result.ospfIfaceA = (hostA.interfaces || []).find((i) => i.name === ifaceA) || null;
    result.ospfIfaceB = (hostB.interfaces || []).find((i) => i.name === ifaceB) || null;
  }

  // MPLS VRF: se a iface tem vrf, o link é CE-facing L3VPN
  if (ifaceA) {
    const i = (hostA.interfaces || []).find((x) => x.name === ifaceA);
    if (i?.vrf) result.vrfA = i.vrf;
  }
  if (ifaceB) {
    const i = (hostB.interfaces || []).find((x) => x.name === ifaceB);
    if (i?.vrf) result.vrfB = i.vrf;
  }

  return result;
}

function PolicyLine({ label, value, tone = 'default' }) {
  if (!value) return null;
  const tones = {
    default: '#94a3b8',
    in: '#60a5fa',
    out: '#34d399',
    warn: '#fbbf24',
  };
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
      <Typography variant="caption" sx={{ color: tones[tone], fontWeight: 700, minWidth: 60 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}

function BgpPeerSide({ host, peer }) {
  if (!peer) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 1, bgcolor: 'rgba(230,126,34,0.08)', border: '1px solid rgba(230,126,34,0.3)', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#e67e22' }}>
        {host?.label} · {peer.ip}
      </Typography>
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
        {peer.remote_as && <Chip label={`AS${peer.remote_as}`} size="small" sx={{ height: 16, fontSize: 9, width: 'fit-content' }} />}
        <PolicyLine label="IN ←" value={[peer.route_map_in && `rm:${peer.route_map_in}`, peer.prefix_list_in && `pl:${peer.prefix_list_in}`, peer.as_path_filter_in && `as:${peer.as_path_filter_in}`].filter(Boolean).join(' · ')} tone="in" />
        <PolicyLine label="OUT →" value={[peer.route_map_out && `rm:${peer.route_map_out}`, peer.prefix_list_out && `pl:${peer.prefix_list_out}`, peer.as_path_filter_out && `as:${peer.as_path_filter_out}`].filter(Boolean).join(' · ')} tone="out" />
        <PolicyLine label="max-prefix" value={peer.max_prefixes ? `${peer.max_prefixes.limit} (${peer.max_prefixes.action})` : null} tone="warn" />
        {peer.ebgp_multihop && <PolicyLine label="multihop" value={String(peer.ebgp_multihop)} />}
        {peer.bfd_enabled && <PolicyLine label="BFD" value="enabled" tone="in" />}
        {peer.route_reflector_client && <PolicyLine label="role" value="RR client" tone="warn" />}
        {peer.timers && <PolicyLine label="timers" value={`keep ${peer.timers.keepalive}s · hold ${peer.timers.hold}s`} />}
      </Box>
    </Box>
  );
}

function OspfIfaceSide({ host, iface }) {
  if (!iface) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 1, bgcolor: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9b59b6' }}>
        {host?.label} · {iface.name}
      </Typography>
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
        {iface.ospf_area && <PolicyLine label="area" value={iface.ospf_area} tone="warn" />}
        {iface.ospf_cost != null && <PolicyLine label="cost" value={String(iface.ospf_cost)} />}
        {iface.ospf_network_type && <PolicyLine label="net-type" value={iface.ospf_network_type} />}
        {iface.ospf_hello_interval && <PolicyLine label="hello" value={`${iface.ospf_hello_interval}s`} />}
        {iface.ospf_dead_interval && <PolicyLine label="dead" value={`${iface.ospf_dead_interval}s`} />}
        {iface.ospf_authentication && <PolicyLine label="auth" value={iface.ospf_authentication} tone="in" />}
        {iface.ospf_bfd && <PolicyLine label="BFD" value="enabled" tone="in" />}
      </Box>
    </Box>
  );
}

function LagBundleSide({ label, side }) {
  if (!side) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 1, bgcolor: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#60a5fa' }}>
        {label} · {side.name}
      </Typography>
      <Box sx={{ mt: 0.3, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip label={`${(side.members || []).length} members`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#1e3a8a', color: '#dbeafe' }} />
        {side.mode && <Chip label={side.mode.toUpperCase()} size="small" sx={{ height: 16, fontSize: 9 }} />}
      </Box>
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.1 }}>
        {(side.members || []).slice(0, 10).map((m) => (
          <Typography key={m} variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
            ● {m}
          </Typography>
        ))}
        {(side.members || []).length > 10 && (
          <Typography variant="caption" color="text.secondary">
            +{(side.members || []).length - 10} mais
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ProtocolDetails({ edge, hostA, hostB }) {
  const { bgpPeerA, bgpPeerB, ospfIfaceA, ospfIfaceB, vrfA, vrfB } = findEdgeProtocolDetails(edge, hostA, hostB);
  const kinds = edge?.kinds || [];
  const lag = edge?.lag;

  const hasBgp = bgpPeerA || bgpPeerB;
  const hasOspf = ospfIfaceA || ospfIfaceB;
  const hasVrf = vrfA || vrfB;
  const hasLag = lag && (lag.a?.members?.length || lag.b?.members?.length);

  if (!hasBgp && !hasOspf && !hasVrf && !hasLag) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {hasLag && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ color: '#60a5fa', fontSize: 10 }}>
            LAG bundle (todos os membros carregam esse link)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
            <LagBundleSide label={hostA?.label} side={lag.a} />
            <LagBundleSide label={hostB?.label} side={lag.b} />
          </Box>
        </Box>
      )}
      {kinds.includes('bgp') && hasBgp && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ color: '#e67e22', fontSize: 10 }}>
            BGP session
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
            <BgpPeerSide host={hostA} peer={bgpPeerA} />
            <BgpPeerSide host={hostB} peer={bgpPeerB} />
          </Box>
        </Box>
      )}
      {kinds.includes('ospf_adj') && hasOspf && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ color: '#9b59b6', fontSize: 10 }}>
            OSPF adjacency
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
            <OspfIfaceSide host={hostA} iface={ospfIfaceA} />
            <OspfIfaceSide host={hostB} iface={ospfIfaceB} />
          </Box>
        </Box>
      )}
      {hasVrf && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ color: '#2ecc71', fontSize: 10 }}>
            MPLS L3VPN
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
            {vrfA && <Chip label={`${hostA?.label} → VRF ${vrfA}`} size="small" sx={{ bgcolor: 'rgba(46,204,113,0.15)', color: '#2ecc71', fontFamily: 'monospace' }} />}
            {vrfB && <Chip label={`${hostB?.label} → VRF ${vrfB}`} size="small" sx={{ bgcolor: 'rgba(46,204,113,0.15)', color: '#2ecc71', fontFamily: 'monospace' }} />}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function EdgeTrafficDialog({ open, onClose, edge, graph }) {
  const base = useTopologyBase();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [minutes, setMinutes] = useState(60);

  const fetchHistory = async () => {
    if (!base || !edge) return;
    setLoading(true);
    try {
      const res = await api.get(
        `${base}/traffic/history/${edge.id}?minutes=${minutes}`,
      );
      setData(res.data);
    } catch (err) {
      setData({ in: [], out: [], error: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && edge) fetchHistory();
    // eslint-disable-next-line
  }, [open, edge?.id, minutes]);

  // Bucket size por janela — mantém ~40-80 pontos no eixo X
  const bucketSec = minutes <= 15 ? 30 : minutes <= 60 ? 90 : minutes <= 180 ? 300 : 600;

  // Merge in/out em buckets de tempo fixos — garante alinhamento e linha contínua
  const chartData = React.useMemo(() => {
    if (!data) return [];
    const inPts = data.in || [];
    const outPts = data.out || [];
    if (inPts.length === 0 && outPts.length === 0) return [];

    // Determina o range total (min clock de ambas séries)
    const allClocks = [...inPts, ...outPts].map((p) => p.clock);
    const minClock = Math.min(...allClocks);
    const maxClock = Math.max(...allClocks);
    // Start alinhado ao bucket
    const startBucket = Math.floor(minClock / bucketSec) * bucketSec;
    const endBucket = Math.ceil(maxClock / bucketSec) * bucketSec;

    const buckets = new Map();
    for (let t = startBucket; t <= endBucket; t += bucketSec) {
      buckets.set(t, { clock: t, time: formatClock(t), inSum: 0, inCnt: 0, outSum: 0, outCnt: 0 });
    }

    for (const p of inPts) {
      const key = Math.floor(p.clock / bucketSec) * bucketSec;
      const b = buckets.get(key);
      if (b) { b.inSum += p.bps; b.inCnt += 1; }
    }
    for (const p of outPts) {
      const key = Math.floor(p.clock / bucketSec) * bucketSec;
      const b = buckets.get(key);
      if (b) { b.outSum += p.bps; b.outCnt += 1; }
    }

    return Array.from(buckets.values())
      .map((b) => ({
        clock: b.clock,
        time: b.time,
        in: b.inCnt > 0 ? b.inSum / b.inCnt : null,
        out: b.outCnt > 0 ? b.outSum / b.outCnt : null,
      }))
      .sort((a, b) => a.clock - b.clock);
  }, [data, bucketSec]);

  const inVals = chartData.map((p) => p.in).filter((v) => v != null);
  const outVals = chartData.map((p) => p.out).filter((v) => v != null);
  const peakIn = inVals.length > 0 ? Math.max(...inVals) : 0;
  const peakOut = outVals.length > 0 ? Math.max(...outVals) : 0;
  const avgIn = inVals.length > 0 ? inVals.reduce((a, v) => a + v, 0) / inVals.length : 0;
  const avgOut = outVals.length > 0 ? outVals.reduce((a, v) => a + v, 0) / outVals.length : 0;

  const ep = edge?.endpoints || {};

  // Resolve nomes dos hosts A e B a partir do graph
  const hostA = React.useMemo(() => {
    if (!edge || !graph) return null;
    return (graph.nodes || []).find((n) => String(n.id) === String(edge.source));
  }, [edge, graph]);
  const hostB = React.useMemo(() => {
    if (!edge || !graph) return null;
    return (graph.nodes || []).find((n) => String(n.id) === String(edge.target));
  }, [edge, graph]);

  // O nó do mapa não traz mais peers BGP nem interface completa — só o que a
  // tela desenha. A seção de protocolo precisa dos dois, então busca o detalhe
  // das duas pontas quando o diálogo abre. São duas requisições de ~14 KB
  // contra os 3,4 MB que o mapa carregava pra todos os nós de uma vez.
  const [detA, setDetA] = useState(null);
  const [detB, setDetB] = useState(null);

  useEffect(() => {
    let vivo = true;
    if (!open || !base || !edge) { setDetA(null); setDetB(null); return undefined; }
    const buscar = (id) => api.get(`${base}/host/${id}`)
      .then((r) => r.data)
      .catch(() => null);
    Promise.all([buscar(edge.source), buscar(edge.target)]).then(([a, b]) => {
      if (!vivo) return;
      setDetA(a); setDetB(b);
    });
    return () => { vivo = false; };
  }, [open, base, edge]);

  // Junta o que o mapa tem (nome, papel) com o que veio do detalhe.
  const hostADet = React.useMemo(
    () => (hostA ? { ...hostA, ...(detA || {}) } : null), [hostA, detA]);
  const hostBDet = React.useMemo(
    () => (hostB ? { ...hostB, ...(detB || {}) } : null), [hostB, detB]);

  // Qual lado o backend tá medindo? Source_side vem no dict de traffic, mas aqui
  // vamos inferir: se data.a_iface existe, é A; senão B.
  const measuredSide = data?.a_iface ? 'a' : 'b';
  const measuredHost = measuredSide === 'a' ? hostA : hostB;
  const otherHost    = measuredSide === 'a' ? hostB : hostA;
  const measuredIface = data?.a_iface || ep.a?.iface || ep.b?.iface;
  const measuredHostLabel = measuredHost?.label || '?';
  const otherHostLabel    = otherHost?.label || '?';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          Tráfego de Interface — {edge?.id}
        </Typography>
        <IconButton onClick={fetchHistory} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
        </IconButton>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {edge && (
          <>
            {/* Linha 1: qual direção é qual? Visualização host A ←→ host B */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5,
              p: 1.5, bgcolor: 'action.hover', borderRadius: 1,
            }}>
              <Chip
                label={hostA?.label || '?'}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              {ep.a?.iface && (
                <Typography variant="caption" color="text.secondary">
                  ({ep.a.iface})
                </Typography>
              )}
              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
                <Typography sx={{ color: '#f59e0b', fontWeight: 700 }}>↑ out →</Typography>
                <Typography sx={{ color: '#06b6d4', fontWeight: 700 }}>← in ↓</Typography>
              </Box>
              {ep.b?.iface && (
                <Typography variant="caption" color="text.secondary">
                  ({ep.b.iface})
                </Typography>
              )}
              <Chip
                label={hostB?.label || '?'}
                size="small"
                color="secondary"
                sx={{ fontWeight: 600 }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                medindo em: <b>{measuredHostLabel}</b> ({measuredIface || '?'})
              </Typography>
            </Box>

            {/* Linha 2: kinds + subnet + vlans */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
              {(edge.kinds || []).map((k) => (
                <Chip key={k} label={k} size="small" color="primary" variant="outlined" />
              ))}
              {edge.subnet && <Chip label={edge.subnet} size="small" color="info" />}
              {edge.vlans && edge.vlans.length > 0 && (
                <Chip label={`VLAN ${edge.vlans.join(',')}`} size="small" color="success" />
              )}
            </Stack>

            {/* Seção de protocolo — aparece só se a edge carrega BGP/OSPF/MPLS */}
            <ProtocolDetails edge={edge} hostA={hostADet} hostB={hostBDet} />
          </>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Janela:</Typography>
          <ToggleButtonGroup
            value={minutes}
            exclusive
            size="small"
            onChange={(_, v) => v && setMinutes(v)}
          >
            <ToggleButton value={15}>15m</ToggleButton>
            <ToggleButton value={60}>1h</ToggleButton>
            <ToggleButton value={180}>3h</ToggleButton>
            <ToggleButton value={360}>6h</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flexGrow: 1 }} />
          {chartData.length > 0 && (
            <Stack direction="row" spacing={2}>
              <Typography variant="caption">
                <b>↓ in</b>  peak: {formatBps(peakIn)}  ·  avg: {formatBps(avgIn)}
              </Typography>
              <Typography variant="caption">
                <b>↑ out</b>  peak: {formatBps(peakOut)}  ·  avg: {formatBps(avgOut)}
              </Typography>
            </Stack>
          )}
        </Box>

        <Box sx={{ height: 340 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : chartData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">
                {data?.error ? `Erro: ${data.error}` : 'Sem dados históricos pra essa interface.'}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="inGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="outGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => formatBps(v)}
                  tick={{ fontSize: 11 }}
                  width={80}
                />
                <Tooltip
                  formatter={(v, name) => [formatBps(v), name]}
                  labelFormatter={(v) => `Horário: ${v}`}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="in"
                  stroke="#06b6d4"
                  strokeWidth={2}
                  fill="url(#inGrad)"
                  name={`↓ IN (chegando em ${measuredHostLabel})`}
                  isAnimationActive={false}
                  connectNulls
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="out"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="url(#outGrad)"
                  name={`↑ OUT (saindo de ${measuredHostLabel})`}
                  isAnimationActive={false}
                  connectNulls
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
